/**
 * Percentage Calculator Tool
 *
 * Calculate percentages: X% of Y, what % is X of Y, percentage change.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export const PercentageCalculatorTool: ITransformerTool = {
  id: 'percentage-calculator',
  name: 'Percentage Calculator',
  description: 'Calculate percentages in multiple modes',
  version: '1.0.0',
  type: 'transformer',
  category: 'Utilities',
  keywords: ['percentage', 'percent', 'calculator', 'math', 'ratio'],

  seo: {
    slug: 'percentage-calculator',
    pageTitle: 'Percentage Calculator - Free Online Tool',
    metaDescription: 'Calculate percentages easily: X% of Y, what percent is X of Y, and percentage change between values. Fast, private, runs in your browser.',
    keywords: ['percentage calculator', 'percent calculator', 'percentage change', 'percent of number', 'percentage formula'],
    relatedTools: ['aspect-ratio-calculator', 'file-size-calculator'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'mode',
      type: 'select',
      label: 'Mode',
      defaultValue: 'percent-of',
      options: ['percent-of', 'what-percent', 'percent-change'],
      description: 'Calculation mode',
    },
    {
      name: 'valueA',
      type: 'number',
      label: 'Value A',
      defaultValue: 25,
      validation: { min: -1000000000, max: 1000000000 },
      description: 'First value (percentage or number depending on mode)',
    },
    {
      name: 'valueB',
      type: 'number',
      label: 'Value B',
      defaultValue: 200,
      validation: { min: -1000000000, max: 1000000000 },
      description: 'Second value',
    },
  ],

  async transform(_input: string, config: Record<string, unknown>): Promise<string> {
    const mode = String(config.mode ?? 'percent-of');
    const a = num(config.valueA, 0);
    const b = num(config.valueB, 0);

    switch (mode) {
      case 'percent-of': {
        // What is A% of B?
        const result = (a / 100) * b;
        return [
          `${a}% of ${b} = ${result}`,
          '',
          `Formula: (${a} / 100) × ${b} = ${result}`,
        ].join('\n');
      }

      case 'what-percent': {
        // What percent is A of B?
        if (b === 0) throw ToolError.validation('Cannot divide by zero — Value B must not be 0');
        const result = (a / b) * 100;
        return [
          `${a} is ${result.toFixed(4)}% of ${b}`,
          '',
          `Formula: (${a} / ${b}) × 100 = ${result.toFixed(4)}%`,
        ].join('\n');
      }

      case 'percent-change': {
        // Percentage change from A to B
        if (a === 0) throw ToolError.validation('Cannot compute change from zero — Value A must not be 0');
        const change = ((b - a) / Math.abs(a)) * 100;
        const direction = change > 0 ? 'increase' : change < 0 ? 'decrease' : 'no change';
        return [
          `Change from ${a} to ${b}: ${change.toFixed(4)}% (${direction})`,
          '',
          `Formula: ((${b} - ${a}) / |${a}|) × 100 = ${change.toFixed(4)}%`,
          `Difference: ${b - a}`,
        ].join('\n');
      }

      default:
        throw ToolError.validation(`Unknown mode: ${mode}`);
    }
  },
};
